<script lang="ts">
  import type { Current } from '../../api/types'
  import { fmtPercent, fmtPressure, fmtTemp, fmtWind } from '../../format'

  /**
   * The observation's four details: label over value, split by Phase 11's
   * 3 px rules (the redesign's `.strip`). On the ground since the tone moved
   * onto the NOW block, on both layouts.
   *
   * The columns are sized to their content and share what is left, rather
   * than 1B's four equal quarters. Measured in Archivo 800 at 14 px, a gusty
   * "SSW 15 mph (G 30)" is 132 px and even "WNW 28 mph" is 92, against a
   * quarter of 420 px that leaves 91 beside the padding. So the gust goes on a
   * line of its own under the wind, and the wind cell takes the width the
   * short humidity and dew point do not need. Phase 11 draws the values at
   * 15 px and the labels in wider caps, which puts the four longest real
   * cells at about 366 px on one row, so below 370 it is 2x2: a 390 px
   * phone's open sheet gets two rows, and a 440 px one gets one.
   */

  let { current }: { current: Current } = $props()

  let cells = $derived([
    {
      label: 'Wind',
      value: fmtWind(current.wind_speed_mph, current.wind_direction_deg, null),
      extra:
        current.wind_speed_mph != null && current.wind_gust_mph != null
          ? `Gusts ${Math.round(current.wind_gust_mph)} mph`
          : null,
    },
    { label: 'Humidity', value: fmtPercent(current.humidity_pct), extra: null },
    { label: 'Dew point', value: fmtTemp(current.dewpoint_f), extra: null },
    { label: 'Pressure', value: fmtPressure(current.pressure_inhg), extra: null },
  ])
</script>

<div class="details">
  <dl>
    {#each cells as cell (cell.label)}
      <div class="cell">
        <dt>{cell.label}</dt>
        <dd>
          {cell.value}{#if cell.extra}<span class="extra">{cell.extra}</span>{/if}
        </dd>
      </div>
    {/each}
  </dl>
</div>

<style>
  .details {
    container-type: inline-size;
  }

  /* The redesign's `.strip`: four cells sized to their content, spread across
     the width, a 3 px rule between each. Below 370 px, two by two. */
  dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 8px;
    margin: 0;
  }

  .cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 6px 4px 6px 10px;
    min-width: 0;
    border-left: 3px solid var(--color-rule);
  }

  .cell:nth-child(2n + 1) {
    border-left: 0;
    padding-left: 0;
  }

  @container (min-width: 370px) {
    dl {
      grid-template-columns: repeat(4, auto);
      justify-content: space-between;
    }

    .cell:nth-child(2n + 1) {
      border-left: 3px solid var(--color-rule);
      padding-left: 10px;
    }

    .cell:first-child {
      border-left: 0;
      padding-left: 0;
    }
  }

  dt {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    display: flex;
    flex-direction: column;
  }

  .extra {
    font-size: 11px;
    font-weight: 600;
    color: var(--color-muted);
  }
</style>
